import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Crown, Check, X, RefreshCw, AlertCircle, Sparkles } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function StatusBadge({ status, cancelAtPeriodEnd }) {
  if (cancelAtPeriodEnd) {
    return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">Canceling</span>;
  }
  const cls =
    status === "active" || status === "trialing" ? "bg-green-500/15 text-green-300 border-green-500/30" :
    status === "past_due" ? "bg-red-500/15 text-red-300 border-red-500/30" :
    status === "canceled" ? "bg-white/10 text-white/60 border-white/20" :
    "bg-white/10 text-white/60 border-white/20";
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cls}`}>{(status || "—").replace(/_/g, " ")}</span>;
}

export default function SubscriptionPanel() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [postCheckoutMsg, setPostCheckoutMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscriptions/me");
      setSub(data.subscription);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sid = params.get("subscription_session");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    setPostCheckoutMsg("Confirming your subscription…");

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/subscriptions/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.subscription) {
          setPostCheckoutMsg(`✓ Subscribed — ${data.subscription.package_name}.`);
          setSub(data.subscription);
          const next = new URLSearchParams(params);
          next.delete("subscription_session");
          setParams(next, { replace: true });
          setTimeout(() => setPostCheckoutMsg(""), 6000);
          return;
        }
        if (attempts < 20) setTimeout(tick, 2000);
      } catch {
        if (!cancelled && attempts < 5) setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async () => {
    if (!sub?.subscription_id) return;
    if (!window.confirm(`Cancel your ${sub.package_name} subscription? You'll keep access until ${fmtDate(sub.current_period_end)}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${sub.subscription_id}/cancel`);
      setSub(data.subscription);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't cancel.");
    } finally { setBusy(false); }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/subscriptions/${sub.subscription_id}/resume`);
      setSub(data.subscription);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't resume.");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-dark p-6" data-testid="subscription-panel">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">Subscription</span>
        </div>
        <button onClick={load} className="text-white/40 hover:text-white p-1.5" title="Refresh" data-testid="subscription-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {postCheckoutMsg && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--if-gold)]/10 border border-[var(--if-gold)]/40 text-sm text-[var(--if-gold)] flex items-center gap-2" data-testid="subscription-confirmation" style={{ fontFamily: "Inter, sans-serif" }}>
          <Sparkles className="w-4 h-4" />
          {postCheckoutMsg}
        </div>
      )}

      {loading && !sub ? (
        <div className="text-xs text-white/40 italic py-4" style={{ fontFamily: "Inter, sans-serif" }}>Loading…</div>
      ) : !sub || sub.status === "canceled" ? (
        <div className="py-2" data-testid="subscription-empty">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-[var(--if-gold)]/70 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-display text-lg text-white mb-1">No active subscription</h3>
              <p className="text-sm text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>
                Choose a Credit Restoration plan to get full bureau analysis, dispute campaigns, and ongoing support.
              </p>
            </div>
          </div>
          <Link to="/services" className="btn-gold !py-2.5 !px-5" data-testid="subscription-cta">
            <Crown className="w-4 h-4" />
            See plans
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="subscription-active">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge status={sub.status} cancelAtPeriodEnd={sub.cancel_at_period_end} />
              </div>
              <h3 className="font-display text-xl text-white">{sub.package_name}</h3>
              <div className="text-2xl font-display text-[var(--if-gold)] mt-1">
                ${sub.amount?.toFixed(2)}<span className="text-sm text-white/45 ml-1">/ month</span>
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-black/20 border border-white/5 text-xs space-y-1" style={{ fontFamily: "Inter, sans-serif" }}>
            {sub.cancel_at_period_end ? (
              <>
                <div className="flex items-center gap-2 text-amber-300">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Cancels on {fmtDate(sub.current_period_end)}
                </div>
                <div className="text-white/55">You'll keep full access until then.</div>
              </>
            ) : (
              <div className="text-white/65">
                Next billing on <span className="text-white">{fmtDate(sub.current_period_end)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {sub.cancel_at_period_end ? (
              <button onClick={resume} disabled={busy} className="btn-gold !py-2 !px-4 !text-[11px]" data-testid="subscription-resume">
                <Check className="w-3.5 h-3.5" />
                {busy ? "…" : "Resume subscription"}
              </button>
            ) : (
              <button onClick={cancel} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/20 text-white/70 text-[11px] hover:bg-white/5 hover:border-white/40 transition-all" data-testid="subscription-cancel">
                <X className="w-3.5 h-3.5" />
                {busy ? "…" : "Cancel subscription"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
